// ============================================================
// MODERN SIDEBAR - Left navigation for modern layout
// File: src/components/ModernSidebar.js
// ============================================================

import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { useLayout } from '../LayoutContext'
import { useTheme } from '../ThemeContext'
import { supabase } from '../supabaseClient'
import { LayoutDashboard, Building2, ClipboardList, PhoneCall, CalendarDays, Calendar, Shield, Menu, X, AlertCircle, UserX, UserCheck, Scale, NotebookPen, ShieldAlert, HelpCircle, KeyRound, BarChart2, Sun, Moon, LogOut, Gauge, GraduationCap, Receipt, Archive, BookUser, ScanSearch } from 'lucide-react'
import toast from 'react-hot-toast'

const SIDEBAR_STYLES = {
  sidebar: (collapsed) => ({
    width: collapsed ? '60px' : '224px',
    height: '100dvh',
    background: '#122036',
    borderRight: '1px solid rgba(125,170,218,0.18)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 200ms ease',
    position: 'relative',
    flexShrink: 0
  }),
  header: {
    padding: '20px 18px 16px',
    borderBottom: '1px solid rgba(125,170,218,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  logoBox: {
    width: '30px',
    height: '30px',
    background: '#3d6494',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  brandName: {
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: '600',
    color: '#edf5ff',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  brandSub: {
    fontSize: '10px',
    color: 'rgba(237,245,255,0.5)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginTop: '2px'
  },
  toggleBtn: {
    padding: '6px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(237,245,255,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    transition: 'all 150ms ease',
    flexShrink: 0
  },
  nav: {
    flex: 1,
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    scrollbarWidth: 'none'
  },
  sectionLabel: {
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    fontSize: '9.5px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(145,169,194,0.9)',
    padding: '8px 18px 4px'
  },
  navItem: (active, collapsed) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '7px 18px',
    margin: '1px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background 150ms ease, color 150ms ease',
    background: active ? 'rgba(61,100,148,0.28)' : 'transparent',
    color: active ? '#edf5ff' : 'rgba(237,245,255,0.92)',
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: active ? '600' : '400',
    border: 'none',
    textAlign: 'left',
    justifyContent: collapsed ? 'center' : 'flex-start',
    width: 'calc(100% - 16px)'
  }),
  footer: {
    padding: '14px 18px',
    borderTop: '1px solid rgba(125,170,218,0.18)'
  },
  userAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#3d6494',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.9)',
    flexShrink: 0
  }
}

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: ['dashboard', 'main', 'abc-law']
  },
  {
    label: 'Operations',
    items: ['new-opening', 'daily-log', 'on-call', 'monthly', 'scheduling', 'contacts']
  },
  {
    label: 'Enforcement',
    items: ['complaints', 'trespass', 'minor-campaign', 'larceny', 'till-detective', 'payments', 'evidence', 'rasp', 'notes']
  },
  {
    label: 'System',
    items: ['admin', 'help']
  }
]

export default function ModernSidebar({ currentView, onNavigate, userRole, isSuperAdmin, organization, user, onSignOut, onSwitchOrganization }) {
  const { sidebarCollapsed, toggleSidebar } = useLayout()
  const { darkMode, toggleDarkMode } = useTheme()
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  // Hover label for icons when the sidebar is collapsed: { label, top, left }
  const [navTip, setNavTip] = useState(null)
  const showNavTip = (e, label) => {
    const r = e.currentTarget.getBoundingClientRect()
    setNavTip({ label, top: r.top + r.height / 2, left: r.right + 10 })
  }
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)

  async function handleChangePassword(e) {
    e.preventDefault()
    if (newPassword.length < 12) { toast.error('Password must be at least 12 characters'); return }
    if (!/[A-Z]/.test(newPassword) || !/[0-9!@#$%^&*]/.test(newPassword)) { toast.error('Password must contain at least one uppercase letter and one number or special character'); return }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return }
    setSavingPassword(true)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    setSavingPassword(false)
    if (error) { toast.error(error.message); return }
    toast.success('Password updated')
    setShowPasswordForm(false)
    setNewPassword('')
    setConfirmPassword('')
  }

  const sidebarBg = darkMode ? '#0a1628' : '#122036'
  const inactiveColor = 'rgba(237,245,255,0.92)'
  const activeColor = '#edf5ff'
  const activeNavBg = 'rgba(61,100,148,0.28)'
  const hoverNavBg = 'rgba(61,100,148,0.12)'

  const isStoreRole = userRole === 'store'
  const showSignalThree = isSuperAdmin || user?.email === 'josh@signalthree.com'

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart2, show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'main', label: 'Inspections', icon: LayoutDashboard, show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'new-opening', label: 'New Opening', icon: Building2, show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'daily-log', label: 'Daily Log', icon: ClipboardList, show: !isStoreRole && organization?.features?.daily_activity_log },
    { id: 'on-call', label: 'On-Call', icon: PhoneCall, show: !isStoreRole && organization?.features?.on_call_management },
    { id: 'monthly', label: 'Monthly', icon: CalendarDays, show: !isStoreRole && organization?.features?.monthly_reports },
    { id: 'scheduling', label: 'Scheduling', icon: Calendar, show: !isStoreRole && organization?.features?.scheduling },
    { id: 'complaints', label: 'Complaints', icon: AlertCircle, show: !isStoreRole && !!organization?.features?.core_inspection },
    { id: 'trespass', label: 'Trespass', icon: UserX, show: organization?.features?.trespass_list },
    { id: 'minor-campaign', label: 'Minor Campaign', icon: UserCheck, show: !isStoreRole && organization?.features?.minor_campaign },
    { id: 'larceny', label: 'Larceny', icon: ShieldAlert, show: !isStoreRole && organization?.features?.larceny_reports },
    { id: 'till-detective', label: 'Till Detective', icon: ScanSearch, show: !isStoreRole && !!organization?.features?.till_detective && (userRole === 'admin' || isSuperAdmin) },
    { id: 'payments', label: 'Payments', icon: Receipt, show: !isStoreRole && organization?.features?.payment_receipts },
    { id: 'evidence', label: 'Evidence', icon: Archive, show: !isStoreRole && organization?.features?.evidence_management },
    { id: 'rasp', label: 'RASP Class', icon: GraduationCap, show: !isStoreRole },
    { id: 'abc-law', label: 'ABC AI', icon: Scale, show: !isStoreRole && !!organization?.features?.abc_law },
    { id: 'notes', label: 'My Notes', icon: NotebookPen, show: !isStoreRole },
    { id: 'contacts', label: 'Contact Info', icon: BookUser, show: !isStoreRole && !!organization?.features?.contact_directory },
    { id: 'admin', label: 'Admin', icon: Shield, show: !isStoreRole && (userRole === 'admin' || isSuperAdmin) },
    { id: 'help', label: 'Help', icon: HelpCircle, show: !isStoreRole }
  ]

  const itemMap = Object.fromEntries(navItems.map(i => [i.id, i]))

  const initials = user?.email
    ? user.email.split('@')[0].slice(0, 2).toUpperCase()
    : 'AB'

  return (
    <div style={{ ...SIDEBAR_STYLES.sidebar(sidebarCollapsed), background: sidebarBg }}>
      {/* Header */}
      <div style={SIDEBAR_STYLES.header}>
        {!sidebarCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            <img
              src="/abcaxis-logo.png"
              alt="ABCAxis"
              onClick={() => onNavigate('main')}
              style={{ height: '36px', width: 'auto', objectFit: 'contain', cursor: 'pointer', filter: 'brightness(0) invert(1)' }}
            />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          style={SIDEBAR_STYLES.toggleBtn}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <Menu size={18} color="rgba(220,232,248,0.6)" /> : <X size={18} color="rgba(220,232,248,0.6)" />}
        </button>
      </div>

      {/* Collapsed-sidebar hover label (rendered on document.body so the sidebar doesn't clip it) */}
      {sidebarCollapsed && navTip && ReactDOM.createPortal(
        <div role="tooltip" style={{
          position: 'fixed', top: navTip.top, left: navTip.left, transform: 'translateY(-50%)',
          zIndex: 3000, pointerEvents: 'none', whiteSpace: 'nowrap',
          background: '#0b1626', color: '#e6eef9', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '6px', padding: '5px 10px', boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", fontSize: '12.5px', fontWeight: '500'
        }}>
          {navTip.label}
        </div>,
        document.body
      )}

      {/* Navigation */}
      <nav style={SIDEBAR_STYLES.nav}>
        {sidebarCollapsed ? (
          // Same order as the expanded sidebar's sections
          NAV_SECTIONS.flatMap(section => section.items.map(id => itemMap[id])).filter(item => item && item.show).map(item => {
            const Icon = item.icon
            const active = currentView === item.id
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                style={{ ...SIDEBAR_STYLES.navItem(active, true), background: active ? activeNavBg : 'transparent', color: active ? activeColor : inactiveColor }}
                onMouseEnter={e => { showNavTip(e, item.label); if (!active) { e.currentTarget.style.background = hoverNavBg; e.currentTarget.style.color = activeColor }}}
                onMouseLeave={e => { setNavTip(null); if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = inactiveColor }}}
                onFocus={e => showNavTip(e, item.label)}
                onBlur={() => setNavTip(null)}
                aria-label={item.label}
              >
                <Icon size={18} style={{ flexShrink: 0 }} />
              </button>
            )
          })
        ) : (
          NAV_SECTIONS.map(section => {
            const visible = section.items
              .map(id => itemMap[id])
              .filter(item => item && item.show)
            if (!visible.length) return null
            return (
              <div key={section.label} style={{ marginBottom: '4px' }}>
                <div style={SIDEBAR_STYLES.sectionLabel}>{section.label}</div>
                {visible.map(item => {
                  const Icon = item.icon
                  const active = currentView === item.id
                  return (
                    <button
                      key={item.id}
                      onClick={() => onNavigate(item.id)}
                      style={{ ...SIDEBAR_STYLES.navItem(active, false), background: active ? activeNavBg : 'transparent', color: active ? activeColor : inactiveColor }}
                      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = hoverNavBg; e.currentTarget.style.color = activeColor }}}
                      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = inactiveColor }}}
                    >
                      <Icon size={16} style={{ flexShrink: 0, opacity: active ? 1 : 0.75 }} />
                      <span>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            )
          })
        )}
      </nav>

      {/* Footer */}
      {sidebarCollapsed ? (
        <div style={{ padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          {showSignalThree && (
            <button
              onClick={() => onNavigate('super-admin')}
              title="Signal Three"
              style={{ background: 'rgba(61,100,148,0.22)', border: 'none', cursor: 'pointer', padding: '7px', color: '#8aadd4', borderRadius: '6px', display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(61,100,148,0.38)'; e.currentTarget.style.color = '#c8daf5' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(61,100,148,0.22)'; e.currentTarget.style.color = '#8aadd4' }}
            >
              <Gauge size={16} />
            </button>
          )}
          <button
            onClick={toggleDarkMode}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '7px', color: inactiveColor, borderRadius: '6px', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => { e.currentTarget.style.background = hoverNavBg; e.currentTarget.style.color = activeColor }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = inactiveColor }}
          >
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            onClick={onSignOut}
            title="Sign Out"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '7px', color: inactiveColor, borderRadius: '6px', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => { e.currentTarget.style.background = hoverNavBg; e.currentTarget.style.color = activeColor }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = inactiveColor }}
          >
            <LogOut size={16} />
          </button>
        </div>
      ) : (
        <div style={SIDEBAR_STYLES.footer}>
          {showPasswordForm ? (
            <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '11px', color: activeColor, fontWeight: '600', marginBottom: '2px', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}>Change Password</div>
              <input
                type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '12px', outline: 'none', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}
              />
              <input
                type="password" placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '12px', outline: 'none', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}
              />
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="submit" disabled={savingPassword} style={{ flex: 1, padding: '5px', borderRadius: '5px', border: 'none', background: 'rgba(61,100,148,0.8)', color: '#fff', fontSize: '11px', cursor: 'pointer', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}>
                  {savingPassword ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowPasswordForm(false); setNewPassword(''); setConfirmPassword('') }} style={{ flex: 1, padding: '5px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.6)', fontSize: '11px', cursor: 'pointer', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* User info row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
                <div style={SIDEBAR_STYLES.userAvatar}>{initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", fontSize: '13px', fontWeight: '600', color: activeColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email?.split('@')[0] || 'Agent'}
                  </div>
                  <div style={{ fontSize: '10px', color: inactiveColor, marginTop: '1px', textTransform: 'capitalize' }}>
                    {userRole === 'inspector' ? 'Agent' : userRole || 'Agent'}
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordForm(true)}
                  title="Change password"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'rgba(255,255,255,0.65)', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
                >
                  <KeyRound size={14} />
                </button>
              </div>

              {/* Signal Three — super admin only */}
              {showSignalThree && (
                <button
                  onClick={() => onNavigate('super-admin')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    width: '100%', marginBottom: '8px',
                    padding: '7px 10px', borderRadius: '6px',
                    background: 'rgba(61,100,148,0.22)',
                    border: '1px solid rgba(90,127,173,0.3)',
                    color: '#8aadd4', cursor: 'pointer',
                    fontSize: '12px', fontWeight: '600',
                    fontFamily: "'Source Sans 3',system-ui,sans-serif",
                    letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(61,100,148,0.38)'; e.currentTarget.style.color = '#c8daf5' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(61,100,148,0.22)'; e.currentTarget.style.color = '#8aadd4' }}
                >
                  <Gauge size={14} style={{ flexShrink: 0 }} />
                  Signal Three
                </button>
              )}

              {/* Divider */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', marginBottom: '10px' }} />

              {/* Controls row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Org name / switcher */}
                {showSignalThree ? (
                  <button
                    onClick={onSwitchOrganization}
                    title={organization ? 'Switch Organization' : 'Select Organization'}
                    style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', color: inactiveColor, fontSize: '11px', padding: '5px 8px', cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}
                    onMouseEnter={e => { e.currentTarget.style.background = hoverNavBg; e.currentTarget.style.color = activeColor }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = inactiveColor }}
                  >
                    🔄 {organization ? organization.name : 'Select org…'}
                  </button>
                ) : (
                  <div style={{ flex: 1, fontSize: '11px', color: 'rgba(220,232,248,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Source Sans 3',system-ui,sans-serif" }}>
                    {organization?.name || ''}
                  </div>
                )}

                {/* Dark mode toggle */}
                <button
                  onClick={toggleDarkMode}
                  title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'rgba(255,255,255,0.65)', borderRadius: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
                >
                  {darkMode ? <Sun size={15} /> : <Moon size={15} />}
                </button>

                {/* Sign out */}
                <button
                  onClick={onSignOut}
                  title="Sign Out"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'rgba(255,255,255,0.65)', borderRadius: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
                >
                  <LogOut size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
